
import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Flag, InfoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Question } from "@/types/exam";
import QuestionTranslationButton from "./QuestionTranslationButton";
import QuestionHintButton from "./QuestionHintButton";
import MathRenderer from "./MathRenderer";

interface QuestionCardProps {
  question: Question;
  currentQuestionIndex: number;
  selectedAnswer: number | null;
  onSelectAnswer: (questionId: string | number, answerIndex: number) => void;
  flaggedQuestions: Record<string, boolean>;
  onFlagQuestion: (questionId: string | number) => void;
  isLocked?: boolean;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  currentQuestionIndex,
  selectedAnswer,
  onSelectAnswer,
  flaggedQuestions,
  onFlagQuestion,
  isLocked = false,
}) => {
  const [translatedQuestion, setTranslatedQuestion] = useState<string | null>(null);
  const [translatedOptions, setTranslatedOptions] = useState<string[] | null>(null);
  const [showTranslation, setShowTranslation] = useState<boolean>(false);

  // Reset translation state when question changes
  useEffect(() => {
    setTranslatedQuestion(null);
    setTranslatedOptions(null);
    setShowTranslation(false);
  }, [question.id]);

  if (!question || !question.options) {
    return null;
  }

  // Use the direct marks value from the question without any calculations
  const marks = question.marks || 1; // Default to 1 if not provided
  const negativeMarks = question.negative_marks || 0.25; // Default to 0.25 if not provided

  // Format marks to avoid excessive decimal places
  const formatMarks = (value: number): string => {
    if (Number.isInteger(value)) {
      return value.toString();
    }
    // For decimal values, limit to 2 decimal places
    return value.toFixed(2);
  };

  const handleTranslation = (newTranslatedQuestion: string, newTranslatedOptions: string[]) => {
    setTranslatedQuestion(newTranslatedQuestion);
    setTranslatedOptions(newTranslatedOptions);
    setShowTranslation(true);
  };

  const toggleTranslation = () => {
    setShowTranslation(!showTranslation);
  };

  // Check if content potentially has math expressions
  const hasMathContent = (text: string): boolean => {
    return text.includes('$') || text.includes('\\(') || text.includes('\\[');
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground font-medium">
              {currentQuestionIndex + 1}
            </span>
            <Badge 
              variant="outline" 
              className={`capitalize ${
                question.difficulty === "easy" 
                  ? "border-green-500 text-green-700" 
                  : question.difficulty === "medium" 
                    ? "border-orange-500 text-orange-700" 
                    : "border-red-500 text-red-700"
              }`}
            >
              {question.difficulty}
            </Badge>
            <Badge variant="outline" className="ml-2">
              +{formatMarks(marks)} marks
            </Badge>
            <Badge variant="outline" className="text-red-500">
              -{formatMarks(negativeMarks)} marks
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onFlagQuestion(question.id)}
            disabled={isLocked}
            className={flaggedQuestions[question.id as string] ? "text-amber-500" : "text-gray-500"}
          >
            <Flag className={`h-5 w-5 ${flaggedQuestions[question.id as string] ? "fill-amber-500" : ""}`} />
          </Button>
        </div>
        
        <div className="my-4">
          {question.metadata?.passage && (
            <div className="bg-muted p-3 rounded-md mb-4 text-sm">
              <p className="font-semibold mb-1">Passage:</p>
              <div className="prose prose-sm max-w-none">
                <MathRenderer text={question.metadata.passage} />
              </div>
            </div>
          )}
          
          {/* Original question with math rendering */}
          <div className="text-lg text-black">
            <MathRenderer text={question.question} />
          </div>
          
          {/* Show translated question when available and toggled */}
          {translatedQuestion && showTranslation && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-600 text-lg">{translatedQuestion}</p>
            </div>
          )}
        </div>
        
        <div className="space-y-3 mt-6">
          {question.options.map((option, index) => (
            <button
              key={index}
              className={`w-full text-left p-4 rounded-md border border-border transition-colors flex items-start relative ${
                selectedAnswer === index
                  ? "bg-primary/10 border-primary"
                  : "hover:bg-accent"
              }`}
              onClick={() => onSelectAnswer(question.id, index)}
              disabled={isLocked}
            >
              <span className="font-medium mr-4 inline-flex items-center justify-center h-6 w-6 rounded-full bg-muted text-muted-foreground">
                {String.fromCharCode(65 + index)}
              </span>
              <span className="text-black">
                <MathRenderer text={option} />
              </span>
              
              {/* Show translated option if translation is shown */}
              {showTranslation && translatedOptions && translatedOptions[index] && (
                <div className="block text-blue-600 text-sm mt-1 ml-10">
                  {translatedOptions[index]}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap justify-between items-center mt-6 pt-4 border-t gap-4">
          <QuestionTranslationButton
            questionText={question.question}
            options={question.options}
            onTranslation={handleTranslation}
            isTranslationVisible={showTranslation}
            onToggleTranslation={toggleTranslation}
          />
          
          <QuestionHintButton
            questionId={question.id}
            subject={question.subject || "general"}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default QuestionCard;
